import { c as _c } from "react/compiler-runtime";
import { Stack, ToggleButton, ToggleButtonGroup, Typography, useColorScheme } from '@mui/material';
import { MPAOperationType, type MPARequest, MPARequestStatus } from '@repo/api-gw-sdk';
import { useMemo, useState } from 'react';
import { DIFF_EDITOR_OPTIONS, MonacoDiffEditor } from '@/app/(dashboard)/db/[id]/Editor/monacoEditor';
import { BackupPolicyCard } from '@/app/(dashboard)/settings/backup-policies/BackupPolicyCard';
import { CopyToClipboardButton } from '@/components/buttons/copyToClipboardButton';
import { MainDialog } from '@/components/dialogs/mainDialog';
import { getBackupPolicyId } from '@/components/dialogs/mpa/mpaRequestEntity';
import { JsonViewerPanel } from '@/components/shared/jsonViewer';
import { useDAL } from '@/contexts/useDAL';
import { useBackupVaults } from '@/data/vaults/useBackupVaults';
import { formatJson } from '@/utils/objectUtils';
import { parseJson } from '@/utils/string';
const parseBody = (request: MPARequest): object | null => request.requestBody ? parseJson<object>(request.requestBody) ?? null : null;
const RequestPathHeader = t0 => {
  const $ = _c(10);
  const {
    request
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      alignItems: "baseline",
      gap: "8px"
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  let t2;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = {
      fontFamily: "monospace"
    };
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  let t3;
  if ($[2] !== request.requestMethod) {
    t3 = <Typography variant="h4" sx={t2}>{request.requestMethod}</Typography>;
    $[2] = request.requestMethod;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  let t4;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = {
      fontFamily: "monospace",
      wordBreak: "break-all"
    };
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  let t5;
  if ($[5] !== request.requestPath) {
    t5 = <Typography variant="body1" sx={t4}>{request.requestPath}</Typography>;
    $[5] = request.requestPath;
    $[6] = t5;
  } else {
    t5 = $[6];
  }
  let t6;
  if ($[7] !== t3 || $[8] !== t5) {
    t6 = <Stack direction="row" sx={t1}>{t3}{t5}</Stack>;
    $[7] = t3;
    $[8] = t5;
    $[9] = t6;
  } else {
    t6 = $[9];
  }
  return t6;
};

// Default renderer: pretty-printed request body, or an empty-state message.
// Used as the fallback whenever a typed renderer can't display its entity
// (e.g. a delete-policy request whose target policy has already been purged).
const JsonRequestBody = t0 => {
  const $ = _c(5);
  const {
    request
  } = t0;
  let t1;
  if ($[0] !== request) {
    t1 = parseBody(request);
    $[0] = request;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const body = t1;
  if (body) {
    let t2;
    if ($[2] !== body) {
      t2 = <JsonViewerPanel data={body} />;
      $[2] = body;
      $[3] = t2;
    } else {
      t2 = $[3];
    }
    return t2;
  }
  let t2;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <Typography variant="body1" sx={{
      color: "text.secondary"
    }}>Request body is empty</Typography>;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  return t2;
};
const DeleteBackupPolicyRequestBody = ({
  request
}: {
  request: MPARequest;
}) => {
  const dal = useDAL();
  const policyId = getBackupPolicyId(request);
  const {
    data: policy,
    isLoading
  } = dal.backupPolicy.useGet(policyId);
  const {
    vaults
  } = useBackupVaults();
  if (isLoading) {
    return null;
  }
  if (!policy) {
    return <JsonRequestBody request={request} />;
  }
  return <BackupPolicyCard policy={policy} vaults={vaults} defaultExpanded />;
};
const UpdateBackupPolicyRequestBody = ({
  request,
  view
}: {
  request: MPARequest;
  view: string;
}) => {
  const dal = useDAL();
  const {
    mode
  } = useColorScheme();
  const policyId = getBackupPolicyId(request);
  const {
    data: currentPolicy,
    isLoading
  } = dal.backupPolicy.useGet(policyId);
  const proposed = useMemo(() => parseBody(request), [request]);
  const original = useMemo(() => currentPolicy ? formatJson(currentPolicy) : '', [currentPolicy]);
  const modified = useMemo(() => proposed ? formatJson(proposed) : '', [proposed]);
  if (isLoading) {
    return null;
  }
  if (!currentPolicy || !proposed || view === 'json') {
    return <JsonRequestBody request={request} />;
  }
  return <Stack className='h-[500px]'>
      <MonacoDiffEditor height='100%' language='json' theme={mode === 'dark' ? 'vs-dark' : 'light'} original={original} modified={modified} keepCurrentOriginalModel keepCurrentModifiedModel options={DIFF_EDITOR_OPTIONS} />
    </Stack>;
};
interface RequestBodyProps {
  request: MPARequest;
  view: string;
}
interface RequestDetailsView {
  key: string;
  label: string;
}
interface RequestDetailsDescriptor {
  // First entry is the default view selected when the dialog opens.
  views: (request: MPARequest) => readonly RequestDetailsView[];
  Renderer: React.ComponentType<RequestBodyProps>;
}
const JSON_VIEW: RequestDetailsView = {
  key: 'json',
  label: 'JSON'
};
const JSON_ONLY: RequestDetailsDescriptor = {
  views: () => [JSON_VIEW],
  Renderer: ({
    request
  }) => <JsonRequestBody request={request} />
};

// Every operation type gets an explicit entry. Default is JSON-only; richer
// renderers (e.g. the policy diff) opt in here.
const requestDetailsByOperation: Record<MPAOperationType, RequestDetailsDescriptor> = {
  [MPAOperationType.MPA_OPERATION_TYPE_MPA_OPERATION_ADD_RESTORE_ACCOUNT]: JSON_ONLY,
  [MPAOperationType.MPA_OPERATION_TYPE_MPA_OPERATION_RESTORE_RESOURCE]: JSON_ONLY,
  [MPAOperationType.MPA_OPERATION_TYPE_MPA_OPERATION_REMOVE_SNAPSHOT_HOLD]: JSON_ONLY,
  [MPAOperationType.MPA_OPERATION_TYPE_MPA_OPERATION_DELETE_BACKUP_POLICY]: {
    views: () => [{
      key: 'card',
      label: 'Card'
    }],
    Renderer: ({
      request
    }) => <DeleteBackupPolicyRequestBody request={request} />
  },
  [MPAOperationType.MPA_OPERATION_TYPE_MPA_OPERATION_UPDATE_BACKUP_POLICY]: {
    // Diff is only meaningful while the request is awaiting approval — once
    // it's approved/denied/expired the "current" policy may have drifted, so
    // we fall back to a plain JSON view.
    views: request => request.status === MPARequestStatus.MPA_REQUEST_STATUS_PENDING_APPROVAL || request.status === MPARequestStatus.MPA_REQUEST_STATUS_CREATED ? [{
      key: 'diff',
      label: 'Diff'
    }, JSON_VIEW] : [JSON_VIEW],
    Renderer: UpdateBackupPolicyRequestBody
  }
};
const getRequestDetailsDescriptor = (request: MPARequest) => requestDetailsByOperation[request.operation] ?? JSON_ONLY;

// Wires view state for a request and returns a ready-to-render toggle.
// Callers decide where to place the toggle (dialog header, inline, etc.)
// and pass `view` down to MpaRequestDetails.
export const useMpaRequestDetailsView = request => {
  const $ = _c(3);
  const views = getRequestDetailsDescriptor(request).views(request);
  const [view, setView] = useState(views[0].key);
  const toggle = views.length > 1 ? <ToggleButtonGroup size="small" exclusive={true} value={view} onChange={(_, next) => {
    if (next) {
      setView(next);
    }
  }} data-testid="mpa-request-view-mode">{views.map(_temp)}</ToggleButtonGroup> : null;
  let t0;
  if ($[0] !== toggle || $[1] !== view) {
    t0 = {
      view,
      toggle
    };
    $[0] = toggle;
    $[1] = view;
    $[2] = t0;
  } else {
    t0 = $[2];
  }
  return t0;
};
export const MpaRequestDetails = t0 => {
  const $ = _c(13);
  const {
    request,
    view
  } = t0;
  let descriptor;
  let t1;
  if ($[0] !== request) {
    descriptor = getRequestDetailsDescriptor(request);
    t1 = descriptor.views(request);
    $[0] = request;
    $[1] = descriptor;
    $[2] = t1;
  } else {
    descriptor = $[1];
    t1 = $[2];
  }
  const views = t1;
  const effectiveView = view ?? views[0].key;
  const {
    Renderer
  } = descriptor;
  let t2;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = {
      gap: "16px"
    };
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== request) {
    t3 = <RequestPathHeader request={request} />;
    $[4] = request;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  let t4;
  if ($[6] !== Renderer || $[7] !== effectiveView || $[8] !== request) {
    t4 = <Renderer request={request} view={effectiveView} />;
    $[6] = Renderer;
    $[7] = effectiveView;
    $[8] = request;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  let t5;
  if ($[10] !== t3 || $[11] !== t4) {
    t5 = <Stack sx={t2}>{t3}{t4}</Stack>;
    $[10] = t3;
    $[11] = t4;
    $[12] = t5;
  } else {
    t5 = $[12];
  }
  return t5;
};
export const MpaRequestDetailsDialog = t0 => {
  const $ = _c(17);
  const {
    isOpen,
    onClose,
    request
  } = t0;
  const body = parseBody(request);
  const {
    view,
    toggle
  } = useMpaRequestDetailsView(request);
  const T0 = MainDialog;
  const t1 = false;
  const t2 = toggle ?? undefined;
  let t3;
  if ($[0] !== onClose || $[1] !== t2) {
    t3 = {
      title: "Request details",
      onClose,
      actions: t2
    };
    $[0] = onClose;
    $[1] = t2;
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  let t4;
  if ($[3] !== onClose) {
    t4 = {
      text: "Close",
      onClick: onClose,
      "data-testid": "close-mpa-request-json-viewer"
    };
    $[3] = onClose;
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  const t5 = body ? <CopyToClipboardButton textToCopy={JSON.stringify(body)} buttonLabel="Copy" /> : undefined;
  let t6;
  if ($[5] !== t4 || $[6] !== t5) {
    t6 = {
      primaryButton: t4,
      children: t5
    };
    $[5] = t4;
    $[6] = t5;
    $[7] = t6;
  } else {
    t6 = $[7];
  }
  let t7;
  if ($[8] !== request || $[9] !== view) {
    t7 = <Stack className="px-[40px]"><MpaRequestDetails request={request} view={view} /></Stack>;
    $[8] = request;
    $[9] = view;
    $[10] = t7;
  } else {
    t7 = $[10];
  }
  let t8;
  if ($[11] !== T0 || $[12] !== isOpen || $[13] !== t3 || $[14] !== t6 || $[15] !== t7) {
    t8 = <T0 isOpen={isOpen} isLoading={t1} header={t3} footer={t6}>{t7}</T0>;
    $[11] = T0;
    $[12] = isOpen;
    $[13] = t3;
    $[14] = t6;
    $[15] = t7;
    $[16] = t8;
  } else {
    t8 = $[16];
  }
  return t8;
};
function _temp(v) {
  return <ToggleButton key={v.key} value={v.key}>{v.label}</ToggleButton>;
}